import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { WellsApiService } from '../../services/wells-api.service';
import { WellViewMappingService } from '../../services/wellview-mapping.service';
import { NotificationService } from '../../services/notification.service';
import { ConfirmationService } from '../../services/confirmation.service';
import { LoadingService } from '../../services/loading.service';
import { TabNavigationService } from '../../services/tab-navigation.service';
import { HttpService } from '../../core/services/http.service';
import { UnitConversionService } from '../../services/unit-conversion.service';
import { trigger, state, style, transition, animate } from '@angular/animations';

@Component({
    selector: 'app-approval-flow',
    templateUrl: './approval-flow.component.html',
    styleUrls: ['./approval-flow.component.scss'],
    standalone: false,
    animations: [
        trigger('slideDown', [
            state('collapsed', style({
                height: '0',
                overflow: 'hidden',
                opacity: '0'
            })),
            state('expanded', style({
                height: '*',
                overflow: 'visible',
                opacity: '1'
            })),
            transition('collapsed <=> expanded', animate('300ms ease-in-out'))
        ])
    ]
})
export class ApprovalFlowComponent implements OnInit {
    public approvalData: any = null;
    public loading: boolean = false;
    public showWellViewData: boolean = false;
    public wellViewData: any[] = [];
    public wellViewDataLoading: boolean = false;
    public wellViewDataError: string = '';
    public isExecuting: boolean = false;
    public executionResult: any = null;
    public isExecuted: boolean = false;
    public schemaType: string = ''; // Store the schema type
    public wellName: string = ''; // Store the well name from route
    private selectedIdRecData: any = null;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private wellsApiService: WellsApiService,
        private wellViewMappingService: WellViewMappingService,
        private notificationService: NotificationService,
        private confirmationService: ConfirmationService,
        private loadingService: LoadingService,
        private tabNavigationService: TabNavigationService,
        private httpService: HttpService,
        public unitConversionService: UnitConversionService
    ) {
        // Get the navigation state
        const navigation = this.router.getCurrentNavigation();
        if (navigation?.extras?.state) {
            this.approvalData = navigation.extras.state['prepareData'];
            this.schemaType = navigation.extras.state['schemaType'] || '';
        }
    }

    ngOnInit(): void {
        console.log('[ApprovalFlow] Schema:', this.schemaType, '| BU:', this.approvalData?.bu);
        
        // Only check if BU is selected - this is the minimum requirement
        const hasBuInSession = sessionStorage.getItem('selectedBU');
        
        // If no BU selected, redirect to home
        if (!hasBuInSession) {
            this.router.navigate(['/wells']);
            return;
        }
        
        // Retrieve selected idrec data from localStorage
        const storedIdRec = localStorage.getItem('selectedIdRec');
        if (storedIdRec) {
            try {
                this.selectedIdRecData = JSON.parse(storedIdRec);
            } catch (e) {
                console.error('Error parsing selectedIdRec from localStorage:', e);
            }
        }
        
        // Get well name from route params
        this.route.params.subscribe(params => {
            this.wellName = params['wellName'] || '';
        });
        
        // If no data was passed via navigation state, redirect to /wells
        // This handles page refresh scenarios where navigation state is lost
        if (!this.approvalData) {
            this.router.navigate(['/wells']);
            return;
        }
        
        // Log fields to update for debugging
        if (this.approvalData?.operations?.[0]?.metadata?.fields_to_update) {
            console.log('[ApprovalFlow] Fields to update:', this.approvalData.operations[0].metadata.fields_to_update);
        }
    }

    public getFieldUpdateKeys(fieldUpdates: any): string[] {
        return fieldUpdates ? Object.keys(fieldUpdates) : [];
    }

    public getActionLabel(action: string): string {
        switch(action) {
            case 'PUT':
                return 'Update';
            case 'POST':
                return 'Create';
            case 'DELETE':
                return 'Delete';
            default:
                return action;
        }
    }

    public getFieldsSectionTitle(action: string): string {
        switch(action) {
            case 'PUT':
                return 'Fields Updated';
            case 'POST':
                return 'Fields Created';
            case 'DELETE':
                return 'Fields';
            default:
                return 'Fields Updated';
        }
    }

    public formatFieldName(fieldName: string): string {
        // Try to get display label from FIELD_MAPPINGS first
        const displayLabel = this.getDisplayLabel(fieldName);
        if (displayLabel && displayLabel !== fieldName) {
            return displayLabel;
        }
        
        // Fallback: Convert field names like "dttmend" to "End Date"
        const fieldNameMap: { [key: string]: string } = {
            'dttmend': 'End Date',
            'dttmstart': 'Start Date',
            'depthbtm': 'Bottom Depth',
            'depthtop': 'Top Depth',
            'sz': 'Size',
            'phase_number': 'Phase Number',
            'hole_size': 'Hole Size'
        };
        return fieldNameMap[fieldName] || fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }

    public onBackToList(): void {
        this.router.navigate(['/wells']);
    }

    public onGoToNextSchema(): void {
        // Get well name from route, approvalData, or executionResult
        let wellName = this.wellName 
            || this.approvalData?.well_name 
            || this.executionResult?.well_name 
            || this.approvalData?.well_ida;
        
        if (!wellName) {
            this.notificationService.error('Cannot navigate: Well name not found');
            this.router.navigate(['/wells']);
            return;
        }

        // Fetch latest well data to get updated stages_completed
        this.httpService.getWellsList(1, 20, wellName).subscribe({
            next: (response: any) => {
                
                if (response.wells && response.wells.length > 0) {
                    const well = response.wells[0];
                    // Ensure stagesCompleted is always an array
                    let stagesCompleted = well.stages_completed || [];
                    if (!Array.isArray(stagesCompleted)) {
                        stagesCompleted = [];
                    }
                    
                    // Store stages_completed in the tab navigation service
                    this.tabNavigationService.setStagesCompleted(stagesCompleted);
                    
                    // Get BU from sessionStorage
                    const bu = sessionStorage.getItem('selectedBU') || 'ABU';
                    
                    // Fetch schemas to find the next incomplete one
                    this.wellsApiService.getSchemaList(wellName, bu).subscribe({
                        next: (schemasResponse: any) => {
                            const schemas = Array.isArray(schemasResponse) ? schemasResponse : (schemasResponse.schemas || []);
                            
                            // Get the next incomplete schema
                            const nextIncompleteSchema = this.findNextIncompleteSchema(schemas, stagesCompleted, bu);
                            
                            if (nextIncompleteSchema) {
                                // Set the selected schema in the service
                                this.tabNavigationService.setSelectedSchema(
                                    nextIncompleteSchema.displayName,
                                    nextIncompleteSchema.apiName
                                );
                                
                                // Navigate to well detail page
                                this.router.navigate(['/wells', wellName]);
                            } else {
                                // All schemas completed
                                this.notificationService.success('All schemas have been completed!');
                                this.router.navigate(['/wells', wellName]);
                            }
                        },
                        error: (err: any) => {
                            console.error('Error fetching schemas:', err);
                            // Still navigate back even if schema fetch fails
                            this.router.navigate(['/wells', wellName]);
                        }
                    });
                } else {
                    // Well not found
                    this.router.navigate(['/wells', wellName]);
                }
            },
            error: (err: any) => {
                console.error('Error fetching well data:', err);
                // Still navigate back even if API call fails
                this.router.navigate(['/wells', wellName]);
            }
        });
    }

    /**
     * Find the next schema that is not completed
     * Uses the same logic as well-detail.component for consistency
     */
    private findNextIncompleteSchema(schemas: any[], stagesCompleted: string[], bu: string): any {
        // Validate inputs
        if (!Array.isArray(stagesCompleted)) {
            stagesCompleted = [];
        }
        
        // Schema ordering configuration for ABU
        const SCHEMA_ORDER_ABU = [
            'Well Header',
            'Wellbore',
            'Wellbore Section',
            'Geological Horizon',
            'Tubing',
            'Tubing Components',
            'Rod',
            'Rod Components',
            'Casing',
            'Casing Components',
            'Cement',
            'Cement Stage',
            'Perforation',
            'Other in Hole'
        ];
        
        // Schema ordering configuration for RBU
        const SCHEMA_ORDER_RBU = [
            'Well Header',
            'Wellbore',
            'Wellbore Section',
            'Geological Horizon',
            'Jobs',
            'Casing',
            'Casing Components',
            'Cement',
            'Cement Stage',
            'Perforation',
            'Other in Hole'
        ];
        
        // Mapping from API schema names to display names for ABU
        const SCHEMA_NAME_MAP_ABU: { [key: string]: string } = {
            'cas': 'Casing',
            'casing': 'Casing',
            'cascomp': 'Casing Components',
            'casing_components': 'Casing Components',
            'casingcomponents': 'Casing Components',
            'cement': 'Cement',
            'cementstage': 'Cement Stage',
            'cement_stage': 'Cement Stage',
            'perforation': 'Perforation',
            'tubing': 'Tubing',
            'tubingcomp': 'Tubing Components',
            'tubing_components': 'Tubing Components',
            'tubingcomponents': 'Tubing Components',
            'rod': 'Rod',
            'rodcomp': 'Rod Components',
            'rod_components': 'Rod Components',
            'rodcomponents': 'Rod Components',
            'wellheader': 'Well Header',
            'well_header': 'Well Header',
            'wellbore': 'Wellbore',
            'wellboresz': 'Wellbore Section',
            'wellbore_sz': 'Wellbore Section',
            'wellboresection': 'Wellbore Section',
            'wellboreformation': 'Geological Horizon',
            'wellbore_formation': 'Geological Horizon',
            'otherinhole': 'Other in Hole',
            'other_in_hole': 'Other in Hole',
        };
        
        // Mapping from API schema names to display names for RBU
        const SCHEMA_NAME_MAP_RBU: { [key: string]: string } = {
            'cas': 'Casing',
            'casing': 'Casing',
            'cascomp': 'Casing Components',
            'casing_components': 'Casing Components',
            'casingcomponents': 'Casing Components',
            'cement': 'Cement',
            'cementstage': 'Cement Stage',
            'cement_stage': 'Cement Stage',
            'perforation': 'Perforation',
            'job': 'Jobs',
            'jobs': 'Jobs',
            'wellheader': 'Well Header',
            'well_header': 'Well Header',
            'wellbore': 'Wellbore',
            'wellboresz': 'Wellbore Section',
            'wellbore_sz': 'Wellbore Section',
            'wellboresection': 'Wellbore Section',
            'wellboreformation': 'Geological Horizon',
            'wellbore_formation': 'Geological Horizon',
            'otherinhole': 'Other in Hole',
            'other_in_hole': 'Other in Hole',
        };
        
        // Select appropriate schema order and name map based on BU
        const SCHEMA_ORDER = bu === 'RBU' ? SCHEMA_ORDER_RBU : SCHEMA_ORDER_ABU;
        const SCHEMA_NAME_MAP = bu === 'RBU' ? SCHEMA_NAME_MAP_RBU : SCHEMA_NAME_MAP_ABU;
        
        // Create a set of available display names from API response
        const availableDisplayNames = new Set<string>();
        for (const apiSchema of schemas) {
            const schemaName = typeof apiSchema === 'string' ? apiSchema : (apiSchema.name || '');
            const normalizedName = schemaName.toLowerCase().replace(/[_\s]+/g, '_');
            const displayName = SCHEMA_NAME_MAP[normalizedName] || this.formatSchemaName(schemaName);
            availableDisplayNames.add(displayName);
        }
        
        // Find the first incomplete enabled schema in the order
        for (const displayName of SCHEMA_ORDER) {
            if (availableDisplayNames.has(displayName)) {
                // Check if this schema is completed
                const isCompleted = this.isSchemaCompleted(displayName, stagesCompleted, SCHEMA_NAME_MAP);
                
                if (!isCompleted) {
                    // Find the API name for this display name
                    let apiName = displayName.toLowerCase().replace(/\s+/g, '_');
                    for (const [key, value] of Object.entries(SCHEMA_NAME_MAP)) {
                        if (value === displayName) {
                            apiName = key;
                            break;
                        }
                    }
                    
                    return { displayName, apiName };
                }
            }
        }
        
        // No incomplete schemas found
        return null;
    }

    /**
     * Check if a schema is completed
     */
    private isSchemaCompleted(displayName: string, stagesCompleted: string[], schemaNameMap: { [key: string]: string }): boolean {
        // Validate stagesCompleted is an array
        if (!stagesCompleted || !Array.isArray(stagesCompleted) || stagesCompleted.length === 0) {
            return false;
        }
        
        // Normalize the display name
        const normalizedDisplayName = displayName.toLowerCase().replace(/[\s_]+/g, '');
        
        // Check if any stage in stagesCompleted matches this schema
        return stagesCompleted.some(stage => {
            const normalizedStage = stage.toLowerCase().replace(/[\s_]+/g, '');
            
            // Direct match
            if (normalizedStage === normalizedDisplayName) {
                return true;
            }
            
            // Check if the stage matches any key in the schema name map that points to this display name
            for (const [key, value] of Object.entries(schemaNameMap)) {
                if (value === displayName) {
                    const normalizedKey = key.toLowerCase().replace(/[\s_]+/g, '');
                    if (normalizedStage === normalizedKey) {
                        return true;
                    }
                }
            }
            
            return false;
        });
    }

    private formatSchemaName(name: string): string {
        return name
            .replace(/_/g, ' ')
            .replace(/\b\w/g, char => char.toUpperCase());
    }

    public toggleWellViewData(): void {
        this.showWellViewData = !this.showWellViewData;
        
        // Load well view data if not already loaded
        if (this.showWellViewData && this.wellViewData.length === 0 && !this.wellViewDataLoading) {
            this.loadWellViewData();
        }
    }

    private loadWellViewData(): void {
        if (!this.approvalData) {
            console.error('No approval data available');
            return;
        }

        if (!this.wellName) {
            console.error('No well name available from route');
            this.wellViewDataError = 'Well name not available';
            return;
        }

        this.wellViewDataLoading = true;
        this.wellViewDataError = '';

        // Use WellViewMappingService to get the correct WellView schema name
        const wellViewSchemaName = this.wellViewMappingService.getWellViewSchemaName(this.schemaType);
        
        // For RBU: wellName from route is the API number, use it if well_ida is not in approvalData
        // For ABU: wellName is the actual well name
        const wellIda = this.approvalData.well_ida || this.wellName;

        this.wellsApiService.getWellViewData(
            wellViewSchemaName, 
            this.wellName, // Pass wellName from route
            wellIda, // Use wellIda from approvalData, or fallback to wellName
            this.approvalData.bu,
            this.selectedIdRecData?.idrec // Pass idrec for schemas that need it
        ).subscribe({
            next: (data: any) => {
                this.wellViewData = data && data.records ? data.records : [];
                this.wellViewDataLoading = false;
            },
            error: (err: any) => {
                this.wellViewDataError = 'Failed to load well view data.';
                this.wellViewDataLoading = false;
                console.error('Error loading well view data:', err);
                this.notificationService.error('Failed to load well view data: ' + (err.error?.detail || err.message || 'Unknown error'));
            }
        });
    }

    public getWellViewDisplayKeys(): string[] {
        if (this.wellViewData && this.wellViewData.length > 0) {
            const firstRecord = this.wellViewData[0];
            // Filter out internal fields that start with 'id' or sys fields
            return Object.keys(firstRecord).filter(key => 
                !key.startsWith('sys') && 
                key !== 'idrec' && 
                key !== 'idrecparent' && 
                key !== 'idwell'
            );
        }
        return [];
    }

    public getDisplayLabel(key: string): string {
        if (!key) return '';
        
        // If the key contains spaces, slashes, or is already a label format, return as-is
        if (/[\s\/\-]/.test(key)) {
            return key;
        }
        
        // STRATEGY 1: Use WellViewMappingService to get the label (primary strategy)
        const wellViewSchemaKey = this.wellViewMappingService.getWellViewSchemaName(this.schemaType);
        if (wellViewSchemaKey) {
            const label = this.wellViewMappingService.getFieldLabelSync(wellViewSchemaKey, key);
            if (label && label !== key) {
                return label;
            }
        }
        
        // STRATEGY 2: Fallback - format the key nicely
        return key
            .replace(/_/g, ' ')
            .replace(/([A-Z])/g, ' $1')
            .toLowerCase()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }

    public formatValue(key: string, value: any): string {
        // Explicitly show 'Null' for actual null values from content
        if (value === null) {
            return 'Null';
        }
        if (value === undefined) {
            return '-';
        }
        
        const dateFields = ['dttmend', 'dttmstart'];
        if (dateFields.includes(key.toLowerCase())) {
            try {
                const date = new Date(value);
                if (!isNaN(date.getTime())) {
                    return date.toISOString().split('T')[0];
                }
            } catch (e) {
                return String(value);
            }
        }
        
        return String(value);
    }

    public isUnitVariantKey(key: string): boolean {
        return this.unitConversionService.isUnitVariantKey(key);
    }

    public setSelectedUnit(unit: string, operationId: string, fieldName?: string): void {
        // If fieldName is provided, use it; otherwise this is for global toggle (backward compatibility)
        const field = fieldName || 'global';
        this.unitConversionService.setSelectedUnit(operationId, field, unit);
    }
    
    public getSelectedUnit(operationId: string, fieldName?: string, operation?: any): string {
        // If fieldName is provided, use it; otherwise this is for global toggle (backward compatibility)
        const field = fieldName || 'global';
        
        // Use operation parameter if provided, otherwise use first operation's data as reference
        const record = operation?.metadata?.middleware_record || operation || this.approvalData?.operations?.[0] || this.executionResult?.results?.[0] || this.wellViewData?.[0];
        return this.unitConversionService.getSelectedUnit(operationId, field, record);
    }

    public getValueForUnit(record: any, baseKey: string, operationId: string): any {
        // CRITICAL: First check if the actual content value is null
        // If content value is null, return 'Null' regardless of converted_values
        if (this.approvalData?.operations) {
            for (const op of this.approvalData.operations) {
                if (op.content) {
                    // Map the display key to actual content key
                    const contentKey = this.mapFieldNameToContentKey(baseKey, op.content);
                    const actualKey = contentKey || baseKey;
                    
                    if (op.content.hasOwnProperty(actualKey)) {
                        if (op.content[actualKey] === null) {
                            return 'Null';
                        }
                    }
                }
            }
        }
        
        // If content is not null, proceed with unit conversion
        return this.unitConversionService.getValueForUnit(operationId, baseKey, record);
    }
    
    /**
     * Check if a display field has unit variants based on converted_values from operation/result metadata
     * This dynamically checks if the field has unit conversions available (ft/m/inch/kg_per_m/etc)
     * Returns false if the actual content value is null (null takes priority)
     */
    public hasUnitVariants(key: string, dataSource?: any): boolean {
        if (!key || !dataSource) return false;
        
        // CRITICAL: Check if actual content value is null first
        // If content value is null, report no unit variants even if converted_values exist
        // This prevents showing "0 m" when the actual value is null
        
        // First, map the display label to the actual content key
        let contentKey = key;
        if (dataSource?.content) {
            const mappedKey = this.mapFieldNameToContentKey(key, dataSource.content);
            if (mappedKey) {
                contentKey = mappedKey;
            }
        }
        
        // Now check if the actual content value is null
        if (dataSource?.content && dataSource.content.hasOwnProperty(contentKey)) {
            if (dataSource.content[contentKey] === null) {
                return false;
            }
        }
        
        // Extract the record that contains converted_values
        // This works for both operation.metadata.middleware_record and executionResult items
        const record = dataSource?.metadata?.middleware_record || dataSource;
        
        return this.unitConversionService.hasUnitVariants(contentKey, record);
    }
    
    /**
     * Get available units for a field
     */
    public getAvailableUnits(fieldName: string, dataSource?: any): string[] {
        if (!fieldName || !dataSource) return [];
        
        const record = dataSource?.metadata?.middleware_record || dataSource;
        return this.unitConversionService.getAvailableUnits(fieldName, record);
    }
    
    /**
     * Get display label for a unit (e.g., "lb_per_ft" -> "lb/ft")
     */
    public getUnitDisplayLabel(unit: string): string {
        return this.unitConversionService.getUnitDisplayLabel(unit);
    }
    
    /**
     * Get the display value for a field from fields_to_update
     * Maps display labels to content keys using WellViewMappingService
     * NOTE: Null content values take priority over converted_values to ensure accuracy
     */
    public getFieldDisplayValue(fieldLabel: string, operation: any): any {
        if (!fieldLabel || !operation || !operation.content) {
            return null;
        }
        
        // Map display label to content key using WellViewMappingService
        const contentKey = this.mapFieldNameToContentKey(fieldLabel, operation.content);
        
        if (!contentKey || !operation.content.hasOwnProperty(contentKey)) {
            return null;
        }
        
        const value = operation.content[contentKey];
        
        // CRITICAL: Check if actual content value is null BEFORE checking unit variants
        // Null content should take priority over converted_values (which might be cached/stale)
        if (value === null) {
            return 'Null';
        }
        
        // Check if this field has unit variants
        const middlewareRecord = operation.metadata?.middleware_record;
        if (middlewareRecord && this.unitConversionService.hasUnitVariants(contentKey, middlewareRecord)) {
            const operationId = operation.metadata?.idwell || operation.idwell || 'default';
            const unitValue = this.unitConversionService.getValueForUnit(operationId, contentKey, middlewareRecord);
            const selectedUnit = this.unitConversionService.getSelectedUnit(operationId, contentKey, middlewareRecord);
            const unitLabel = this.unitConversionService.getUnitDisplayLabel(selectedUnit);
            
            if (unitValue !== undefined && unitValue !== null) {
                return `${unitValue} ${unitLabel}`;
            }
        }
        
        // Format dates if applicable
        if (this.isDateField(contentKey)) {
            return this.formatValue(contentKey, value);
        }
        
        return value;
    }


    /**
     * Map display field names to content keys
     * Uses WellViewMappingService with wv_mappings.json (case-insensitive comparison)
     */
    private mapFieldNameToContentKey(fieldDisplayName: string, content: any): string | null {
        if (!content || !fieldDisplayName) return null;
        
        // STRATEGY 1: Use WellViewMappingService to map display label to content key
        const wellViewSchemaKey = this.wellViewMappingService.getWellViewSchemaName(this.schemaType);
        if (wellViewSchemaKey) {
            const contentKey = this.wellViewMappingService.getContentKeyForLabel(wellViewSchemaKey, fieldDisplayName, content);
            if (contentKey && content.hasOwnProperty(contentKey)) {
                return contentKey;
            }
        }
        
        // STRATEGY 2: Try case-insensitive match in content (fallback)
        const normalizedField = fieldDisplayName.toLowerCase().replace(/[\s_&-]/g, '');
        for (const key of Object.keys(content)) {
            const normalizedKey = key.toLowerCase().replace(/[\s_&-]/g, '');
            if (normalizedKey === normalizedField) {
                return key;
            }
        }
        
        // STRATEGY 3: Try partial match (fallback)
        for (const key of Object.keys(content)) {
            const normalizedKey = key.toLowerCase().replace(/[\s_&-]/g, '');
            if (normalizedKey.includes(normalizedField) || normalizedField.includes(normalizedKey)) {
                return key;
            }
        }
        
        // Log when mapping fails
        console.log(`[Mapping Fail] Field: "${fieldDisplayName}" | Available content keys:`, Object.keys(content).slice(0, 15));
        
        return null;
    }
    
    /**
     * Get the value for a field from operation content with unit conversion support
     */
    public getValueForOperationField(operation: any, field: string, operationId: string): any {
        if (!operation || !field || !operationId) {
            return null;
        }

        // Get the middleware record which contains converted_values
        const record = operation.metadata?.middleware_record || operation;
        
        // FIRST: Check if this field has unit variants in converted_values
        // This takes priority because converted_values has the properly formatted values with units
        if (this.unitConversionService.hasUnitVariants(field, record)) {
            const unitValue = this.unitConversionService.getValueForUnit(operationId, field, record);
            const selectedUnit = this.unitConversionService.getSelectedUnit(operationId, field, record);
            const unitLabel = this.unitConversionService.getUnitDisplayLabel(selectedUnit);
            
            if (unitValue !== undefined && unitValue !== null) {
                return `${unitValue} ${unitLabel}`;
            }
        }
        
        // SECOND: Try to map the field name to content key and get value from content
        const contentKey = this.mapFieldNameToContentKey(field, operation.content);
        if (contentKey && operation.content && operation.content.hasOwnProperty(contentKey)) {
            const value = operation.content[contentKey];
            // Explicitly handle null values
            if (value === null) {
                return 'Null';
            }
            // Format dates if applicable
            if (this.isDateField(contentKey)) {
                return this.formatValue(contentKey, value);
            }
            return value;
        }
        
        // THIRD: Try direct field name match in content (case-insensitive)
        if (operation.content) {
            for (const key of Object.keys(operation.content)) {
                if (key.toLowerCase() === field.toLowerCase()) {
                    const value = operation.content[key];
                    // Explicitly handle null values
                    if (value === null) {
                        return 'Null';
                    }
                    if (this.isDateField(key)) {
                        return this.formatValue(key, value);
                    }
                    return value;
                }
            }
        }
        
        return null;
    }
    
    /**
     * Check if a field is a date field
     */
    private isDateField(fieldName: string): boolean {
        const dateKeywords = ['date', 'dttm', 'time', 'datetime'];
        const lowerField = fieldName.toLowerCase();
        return dateKeywords.some(keyword => lowerField.includes(keyword));
    }
    
    /**
     * Get all content keys for display when fields_to_update is not available
     */
    public getContentKeys(operation: any): string[] {
        if (!operation || !operation.content) return [];
        
        // Filter out internal fields and metadata
        return Object.keys(operation.content).filter(key => 
            key !== 'idrec' && 
            key !== 'idrecparent' && 
            key !== 'idwell' &&
            !key.startsWith('sys') &&
            !key.startsWith('_')
        );
    }

    /**
     * Get UI label for an operation/result based on ui_keys array
     * Custom formatting based on schema type:
     * - Well Header: Show only Wellname value (ignore API Number)
     * - Geological Horizon: Show Surface/Formation + MD with label prefix (e.g., "UPPER ANDERSON, MD: 576.072")
     * - Casing Component & others: Show as key-value pairs (Label: value, ...)
     * Falls back to Record N if ui_keys not available
     */
    public getOperationLabel(operation: any, index: number): string {
        if (!operation) return `Record ${index + 1}`;
        
        // Check for ui_keys array in the operation or result object
        const uiKeys = operation.ui_keys || operation.metadata?.ui_keys;
        
        if (uiKeys && Array.isArray(uiKeys) && uiKeys.length > 0) {
            // Try to detect schema type from operation metadata
            const schemaName = operation.metadata?.schema_name || operation.schema_name || this.schemaType || '';
            const normalizedSchema = schemaName.toLowerCase().replace(/[\s_]/g, '');
            
            // Well Header: Show only Wellname value
            if (normalizedSchema.includes('wellheader')) {
                const wellnameItem = uiKeys.find(item => 
                    item.label?.toLowerCase() === 'wellname'
                );
                if (wellnameItem) {
                    return String(wellnameItem.value);
                }
            }
            
            // Geological Horizon: Format as "Surface/Formation, MD: value"
            // IMPORTANT: Check for both "Surface" and "Formation" labels as they can vary
            if (normalizedSchema.includes('geological') || normalizedSchema.includes('formation') || normalizedSchema.includes('horizon')) {
                // Look for Surface OR Formation (both are valid for geological horizon)
                const surfaceItem = uiKeys.find(item => 
                    item.label?.toLowerCase() === 'surface' || item.label?.toLowerCase() === 'formation'
                );
                const mdItem = uiKeys.find(item => 
                    item.label?.toLowerCase() === 'md'
                );
                
                const parts = [];
                if (surfaceItem) {
                    parts.push(String(surfaceItem.value));
                }
                if (mdItem) {
                    parts.push(`MD: ${mdItem.value}`);
                }
                
                if (parts.length > 0) {
                    return parts.join(', ');
                }
            }
            
            // Casing Component and other schemas: Show as key-value pairs
            // Format: "Label: value, Label2: value2"
            const keyValuePairs = uiKeys
                .filter(item => item.label && item.value !== null && item.value !== undefined)
                .map(item => `${item.label}: ${item.value}`);
            
            if (keyValuePairs.length > 0) {
                return keyValuePairs.join(', ');
            }
        }
        
        // Fallback to Record N if no ui_keys
        return `Record ${index + 1}`;
    }

    public onExecute(): void {
        if (!this.approvalData || this.isExecuting || this.isExecuted) {
            return;
        }
        
        // Show confirmation dialog before starting execute
        this.confirmationService.show({
            title: 'Execute Changes to WellView',
            message: 'Are you sure you want to execute these changes to WellView?',
            warningText: 'This action will permanently write data to WellView and CANNOT be undone.',
            confirmText: 'Execute',
            cancelText: 'Cancel',
            type: 'info',
            confirmButtonType: 'execute',
            confirmCallback: () => {
                this.executeChanges();
            },
            cancelCallback: () => {
                console.log('Execute cancelled by user');
            }
        });
    }

    private executeChanges(): void {
        this.isExecuting = true;
        
        // Show fullscreen loading overlay
        this.loadingService.show('Executing changes to WellView...');
        
        // Map schemaType to API schema name - COMPREHENSIVE mapping to prevent missing endpoint errors
        const SCHEMA_NAME_MAP: { [key: string]: string } = {
            // Well Header
            'well_header': 'wellheader',
            'wellheader': 'wellheader',
            'well-header': 'wellheader',
            'well header': 'wellheader',
            'WellHeader': 'wellheader',
            
            // Wellbore
            'wellbore': 'wellbore',
            'Wellbore': 'wellbore',
            
            // Wellbore Section/Size
            'wellbore section': 'wellbore_sz',
            'wellboresection': 'wellbore_sz',
            'wellbore-section': 'wellbore_sz',
            'wellbore_section': 'wellbore_sz',
            'wellboresz': 'wellbore_sz',
            'wellbore_sz': 'wellbore_sz',
            'wellbore-sz': 'wellbore_sz',
            'wellbore sz': 'wellbore_sz',
            'WellboreSz': 'wellbore_sz',
            'WellboreSection': 'wellbore_sz',
            
            // Geological Horizon/Formation
            'wellboreformation': 'geo_horizon',
            'wellbore_formation': 'geo_horizon',
            'wellbore-formation': 'geo_horizon',
            'wellbore formation': 'geo_horizon',
            'WellboreFormation': 'geo_horizon',
            'geo horizon': 'geo_horizon',
            'geohorizon': 'geo_horizon',
            'geo-horizon': 'geo_horizon',
            'geo_horizon': 'geo_horizon',
            'geological horizon': 'geo_horizon',
            'geologicalhorizon': 'geo_horizon',
            'geological-horizon': 'geo_horizon',
            'geological_horizon': 'geo_horizon',
            'GeologicalHorizon': 'geo_horizon',
            
            // Jobs
            'jobs': 'job',
            'job': 'job',
            'Job': 'job',
            'Jobs': 'job',
            
            // Casing
            'casing': 'casing',
            'cas': 'casing',
            'Cas': 'casing',
            'Casing': 'casing',
            
            // Casing Components
            'casing_component': 'casing_component',
            'casingcomponent': 'casing_component',
            'casing-component': 'casing_component',
            'casing component': 'casing_component',
            'casing_components': 'casing_component',
            'casingcomponents': 'casing_component',
            'casing-components': 'casing_component',
            'casing components': 'casing_component',
            'cascomp': 'casing_component',
            'CasComp': 'casing_component',
            'CasingComponent': 'casing_component',
            'CasingComponents': 'casing_component',
            
            // Cement
            'cement': 'cement',
            'Cement': 'cement',
            
            // Cement Stage
            'cement_stage': 'cement_stage',
            'cementstage': 'cement_stage',
            'cement-stage': 'cement_stage',
            'cement stage': 'cement_stage',
            'CementStage': 'cement_stage',
            
            // Perforation
            'perforation': 'perforation',
            'Perforation': 'perforation',
            
            // Tubing
            'tubing': 'tubing',
            'Tubing': 'tubing',
            'tubing assembly': 'tubing',
            'tubingassembly': 'tubing',
            
            // Tubing Components
            'tubing_comp': 'tubing-components',
            'tubingcomp': 'tubing-components',
            'tubing-comp': 'tubing-components',
            'tubing comp': 'tubing-components',
            'tubing_components': 'tubing-components',
            'tubingcomponents': 'tubing-components',
            'tubing-components': 'tubing-components',
            'tubing components': 'tubing-components',
            'TubingComp': 'tubing-components',
            'TubingComponents': 'tubing-components',
            
            // Rod
            'rod': 'rod_details',
            'Rod': 'rod_details',
            'rod details': 'rod_details',
            'roddetails': 'rod_details',
            'rod-details': 'rod_details',
            'rod_details': 'rod_details',
            'RodDetails': 'rod_details',
            
            // Rod Components
            'rod_comp': 'rod-components',
            'rodcomp': 'rod-components',
            'rod-comp': 'rod-components',
            'rod comp': 'rod-components',
            'rod_components': 'rod-components',
            'rodcomponents': 'rod-components',
            'rod-components': 'rod-components',
            'rod components': 'rod-components',
            'RodComp': 'rod-components',
            'RodComponents': 'rod-components',
            
            // Other in Hole
            'other_in_hole': 'otherinhole',
            'otherinhole': 'otherinhole',
            'other-in-hole': 'otherinhole',
            'other in hole': 'otherinhole',
            'other inhole': 'otherinhole',
            'OtherInHole': 'otherinhole',
            
            // Proposed schemas
            'proposed_tubing': 'proposed_tubing',
            'proposed_casing': 'proposed_casing',
            'proposed_cement': 'proposed_cement',
            'proposed_perforation': 'proposed_perforation'
        };

        let apiSchemaName = this.schemaType;
        const lower = (s: string) => s.replace(/_/g, ' ').toLowerCase();
        for (const key in SCHEMA_NAME_MAP) {
            if (lower(this.schemaType) === lower(key)) {
                apiSchemaName = SCHEMA_NAME_MAP[key];
                break;
            }
        }
        

        
        // Use executeSchemaChanges for all schemas (including wellheader)
        this.wellsApiService.executeSchemaChanges(apiSchemaName, this.approvalData).subscribe({
            next: (response: any) => {
                
                // Normalize the response structure to match template expectations
                this.executionResult = this.normalizeExecutionResponse(response);
                
                this.isExecuting = false;
                this.isExecuted = true;
                this.loadingService.hide();
                this.notificationService.success('Changes executed successfully to WellView');
            },
            error: (err: any) => {
                console.error('Error executing changes:', err);
                console.error('Error details:', err?.error || err?.message || err);
                this.isExecuting = false;
                this.loadingService.hide();
                this.notificationService.error(`Execution failed: ${err?.error?.message || err?.message || 'Unknown error'}`);
            }
        });
    }

    /**
     * Normalize execution response to match template expectations
     * Maps backend field names to frontend expected names
     */
    private normalizeExecutionResponse(response: any): any {
        if (!response) return response;

        // Create normalized structure
        const normalized: any = {
            status: response.status || 'completed',
            well_name: response.wellida || response.well_name,
            bu: response.bu,
            well_id: response.wellida || response.well_id,
            // Map execution_summary to summary
            summary: response.execution_summary || response.summary || {
                total: response.execution_summary?.total || 0,
                successful: response.execution_summary?.successful || 0,
                failed: response.execution_summary?.failed || 0
            },
            // Map execution_results to results and operation_index to operation
            results: (response.execution_results || response.results || []).map((result: any) => ({
                ...result,
                operation: result.operation_index !== undefined ? result.operation_index + 1 : result.operation
            }))
        };

        return normalized;
    }
}
